'use client';

import React, { useState } from 'react';
import { TooltipProvider } from '@wtc/ui/components/tooltip';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { useSidebarResize } from './hooks/useSidebarResize';
import { useChatFiltering } from './hooks/useChatFiltering';
import { useChatNavigation } from './hooks/useChatNavigation';
import { formatDate } from './utils/dateFormatter';
import { truncateTitle } from './utils/stringUtils';
import { MobileDrawer } from './components/MobileDrawer';
import { ResizeHandle } from './components/ResizeHandle';
import { SidebarContent } from './components/SidebarContent';
import type { ChatSidebarTabsProps } from './types';
import { SIDEBAR_LAYOUT } from '../../styles/sidebar-layout';

/**
 * ChatSidebarTabs Component
 * Main component that orchestrates mobile drawer and desktop sidebar
 * Mobile-specific logic is now delegated to MobileDrawer component
 */
export function ChatSidebarTabs({
  chats,
  selectedChatId,
  onSelectChat,
  onCreateChat,
  onRestoreChat,
  onDeleteChat,
  onDeleteAllChats,
  isLoading,
  locale = 'en',
  isOpen = true,
  onClose,
  onLoadMoreChats,
  hasMoreChats = false,
  isLoadingMoreChats = false,
  onOpenSettings,
  onOpenCapabilities,
  mcpConnectionsHref,
  compactMode = false,
  sidebarBehavior = 'responsive',
  meridianLogoUrl = '/meridian.png',
}: ChatSidebarTabsProps) {
  // Desktop sidebar collapse — compact hosts start narrow, but split-fixed (extension sidepanel)
  // must start expanded or users get a 60px strip with no resize handle and no desktop header at <md.
  const [isDesktopCollapsed, setIsDesktopCollapsed] = useState(
    () => compactMode && sidebarBehavior !== 'split-fixed'
  );
  
  // Use custom hooks for state management (split-fixed: allow wider list in extension sidepanel)
  const { sidebarWidth, isResizing, handlePointerDown } = useSidebarResize({
    initialWidth: compactMode ? 170 : 200,
    minWidth: compactMode ? 120 : 120,
    maxWidth:
      sidebarBehavior === 'split-fixed' ? 560 : compactMode ? 280 : 400,
  });
  const { activeTab, setActiveTab, filteredChats, tabs } = useChatFiltering(chats);
  const { handleChatHistoryClick, handleSelectChat } = useChatNavigation(locale);

  /**
   * Use infinite scroll hook for pagination
   * This hook handles all Intersection Observer logic
   * Separation of Duties: Component only needs to attach ref and provide callback
   */
  const loadTriggerRef = useInfiniteScroll({
    onLoadMore: onLoadMoreChats || (async () => {}),
    hasMore: hasMoreChats,
    isLoading: isLoadingMoreChats,
    rootMargin: '100px',
    threshold: 0.1,
  });

  const showMobileDrawer = sidebarBehavior === 'responsive' || sidebarBehavior === 'drawer-only';
  const showDesktopSidebar = sidebarBehavior === 'responsive' || sidebarBehavior === 'split-fixed';
  const desktopSidebarLayoutClass =
    sidebarBehavior === 'split-fixed' ? SIDEBAR_LAYOUT.desktopSplitHost : SIDEBAR_LAYOUT.desktop;

  return (
    <TooltipProvider delayDuration={200} disableHoverableContent>
      {/* Mobile Drawer — not used for split-fixed (extension inline chat list) */}
      {showMobileDrawer && (
        <MobileDrawer
          chats={chats}
          selectedChatId={selectedChatId}
          onSelectChat={onSelectChat}
          onCreateChat={onCreateChat}
          onRestoreChat={onRestoreChat}
          onDeleteChat={onDeleteChat}
          onDeleteAllChats={onDeleteAllChats}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          tabs={tabs}
          filteredChats={filteredChats}
          loadTriggerRef={loadTriggerRef}
          hasMoreChats={hasMoreChats}
          isLoadingMoreChats={isLoadingMoreChats}
          truncateTitle={truncateTitle}
          formatDate={formatDate}
          onOpenSettings={onOpenSettings}
          onOpenCapabilities={onOpenCapabilities}
          mcpConnectionsHref={mcpConnectionsHref}
          onClose={onClose}
          meridianLogoUrl={meridianLogoUrl}
        />
      )}

      {/* Desktop / inline sidebar */}
      {showDesktopSidebar && (
        <div
          className={`${desktopSidebarLayoutClass} group relative overflow-hidden transition-all duration-300`}
          style={{ width: isDesktopCollapsed ? '60px' : `${sidebarWidth}px`, flexShrink: 0 }}
        >
          <SidebarContent
            onCreateChat={onCreateChat}
            onChatHistoryClick={handleChatHistoryClick}
            chats={chats}
            selectedChatId={selectedChatId}
            onSelectChat={handleSelectChat}
            onRestoreChat={onRestoreChat}
            onDeleteChat={onDeleteChat}
            onDeleteAllChats={onDeleteAllChats}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            tabs={tabs}
            filteredChats={filteredChats}
            loadTriggerRef={loadTriggerRef}
            hasMoreChats={hasMoreChats}
            isLoadingMoreChats={isLoadingMoreChats}
            truncateTitle={truncateTitle}
            formatDate={formatDate}
            onOpenSettings={onOpenSettings}
            onOpenCapabilities={onOpenCapabilities}
          mcpConnectionsHref={mcpConnectionsHref}
            onToggleCollapse={() => setIsDesktopCollapsed(!isDesktopCollapsed)}
            isCollapsed={isDesktopCollapsed}
            compactMode={compactMode}
            meridianLogoUrl={meridianLogoUrl}
            forceDesktopHeader={sidebarBehavior === 'split-fixed'}
          />

          {!isDesktopCollapsed && (
            <ResizeHandle isResizing={isResizing} onPointerDown={handlePointerDown} />
          )}
        </div>
      )}
    </TooltipProvider>
  );
}
